/**
 * Geo-localised "Local" page. Cascade:
 *   1. Ask the browser for GPS via `navigator.geolocation`
 *   2. POST /local/here → backend reverse-geocodes and returns
 *      events for the tightest tier that has stories (city → state
 *      → country → global "local" topic)
 *   3. Render the resolved location + a tier badge + the feed
 *
 * When geolocation is denied or unsupported we show a friendly nudge
 * and drop straight to the /topic/local fallback so the page is never
 * empty.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Loader2, Navigation, AlertTriangle, Compass, Search, X,
  Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow,
  CloudLightning, Snowflake, Droplets, Wind, Star, StarOff, Home,
} from "lucide-react";
import { npApi } from "./api";
import EventCard from "./EventCard";
import PublisherWidget from "../../components/publisher/PublisherWidget";
import MyCityStrip from "./MyCityStrip";
import { EmergencyBoardBanner } from "../../components/chatpulse/ChatPulseThread";

const CACHE_KEY = "np.local.cache";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const CACHE_VERSION = 1;

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CACHE_VERSION) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ v: CACHE_VERSION, savedAt: Date.now(), ...payload }),
    );
  } catch {
    /* quota exceeded / private mode — safe to ignore */
  }
}

function clearCache() {
  try { window.localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
}

// ─── Saved / pinned cities ───────────────────────────────────────
const SAVED_CITIES_KEY = "np.local.saved_cities";
const SAVED_CITIES_MAX = 6;

function locationKey(loc) {
  if (!loc) return "";
  // Round to 3 decimals so tiny GPS jitter still keys the same city.
  const lat = Number(loc.lat).toFixed(3);
  const lon = Number(loc.lon).toFixed(3);
  return `${(loc.city || "")}|${(loc.admin1 || "")}|${(loc.country || "")}|${lat},${lon}`;
}

function readSavedCities() {
  try {
    const raw = window.localStorage.getItem(SAVED_CITIES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeSavedCities(list) {
  try {
    window.localStorage.setItem(SAVED_CITIES_KEY, JSON.stringify(list.slice(0, SAVED_CITIES_MAX)));
  } catch { /* noop */ }
}

// ─── Home city (default city on load) ────────────────────────────
const HOME_CITY_KEY = "np.local.home_city_key";

function readHomeCityKey() {
  try {
    return window.localStorage.getItem(HOME_CITY_KEY) || null;
  } catch {
    return null;
  }
}

function writeHomeCityKey(key) {
  try {
    if (key) window.localStorage.setItem(HOME_CITY_KEY, key);
    else window.localStorage.removeItem(HOME_CITY_KEY);
  } catch { /* noop */ }
}

const WEATHER_ICON = {
  "sun":              Sun,
  "cloud":            Cloud,
  "cloud-sun":        CloudSun,
  "cloud-fog":        CloudFog,
  "cloud-drizzle":    CloudDrizzle,
  "cloud-rain":       CloudRain,
  "cloud-rain-heavy": CloudRain,
  "cloud-lightning":  CloudLightning,
  "snowflake":        Snowflake,
  "snow":             CloudSnow,
};

const TIER_LABEL = {
  city:    "In your city",
  admin1:  "In your state / region",
  country: "In your country",
  topic:   "General local news",
  none:    "No local news yet",
};

const TIER_BADGE_STYLE = {
  city:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  admin1:  "bg-blue-100 text-blue-800 border-blue-200",
  country: "bg-amber-100 text-amber-800 border-amber-200",
  topic:   "bg-slate-100 text-slate-700 border-slate-200",
  none:    "bg-slate-100 text-slate-500 border-slate-200",
};

function summariseLocation(loc) {
  if (!loc) return "";
  const parts = [loc.city, loc.admin1, loc.country_name || loc.country].filter(Boolean);
  return parts.join(", ");
}


/**
 * Compose a Publisher location_id from the resolved GPS place.
 * Format matches the seed data: `${countryISO2}-${admin1Slug}-${citySlug}`.
 * The Publisher widget-feed API does prefix-matching so passing the
 * finest granularity still fires state and country-level matches.
 */
function publisherLocationId(loc) {
  if (!loc) return null;
  const slug = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  const parts = [loc.country, slug(loc.admin1), slug(loc.city)].filter(Boolean);
  return parts.length ? parts.join("-") : null;
}


function humidityHint(h) {
  if (h == null) return "";
  if (h >= 80) return " · humid";
  if (h >= 60) return " · muggy";
  if (h <= 25) return " · dry";
  return "";
}


function WeatherPill({ weather }) {
  if (!weather || weather.temp_c == null) return null;
  const Icon = WEATHER_ICON[weather.icon] || Cloud;
  const t = Math.round(weather.temp_c);
  const label = weather.label || "";
  return (
    <span
      className="hidden md:inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-white text-slate-800 border border-slate-200 shadow-sm"
      data-testid="np-local-weather"
      title={`Feels: ${label}${weather.humidity != null ? ` · ${weather.humidity}% humidity` : ""}${weather.wind_kmh != null ? ` · wind ${Math.round(weather.wind_kmh)} km/h` : ""}`}
    >
      <Icon className="w-3.5 h-3.5 text-blue-600" />
      <span data-testid="np-local-weather-temp">{t}°C</span>
      <span className="text-slate-400">·</span>
      <span className="text-slate-600">{label.toLowerCase()}{humidityHint(weather.humidity)}</span>
    </span>
  );
}

export default function NewsPulseLocal() {
  const [state, setState] = useState(() => {
    const cached = readCache();
    if (cached && cached.location) {
      return {
        phase: "ready",
        location: cached.location,
        tier: cached.tier,
        events: cached.events || [],
        weather: cached.weather || null,
        mode: cached.mode || "gps",
        errorMsg: null,
        fromCache: true,
      };
    }
    // No fresh cache — check if the reader has a "home" city pinned.
    // If so, seed the state so the geolocation prompt is skipped and
    // /local opens straight into the home city.
    const homeKey = readHomeCityKey();
    const savedCities = readSavedCities();
    const home = homeKey ? savedCities.find((c) => locationKey(c) === homeKey) : null;
    if (home) {
      return {
        phase: "resolving",
        location: {
          city: home.city, admin1: home.admin1, country: home.country,
          country_name: home.country_name, lat: home.lat, lon: home.lon,
        },
        tier: null, events: [], weather: null,
        mode: "home",
        errorMsg: null,
        fromCache: false,
      };
    }
    return {
      phase: "idle",
      location: null, tier: null, events: [], weather: null,
      mode: null, errorMsg: null, fromCache: false,
    };
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savedCities, setSavedCities] = useState(() => readSavedCities());
  const [homeKey, setHomeKey] = useState(() => readHomeCityKey());
  const requestedRef = useRef(false);

  const currentKey = state.location ? locationKey(state.location) : "";
  const isCurrentSaved = savedCities.some((c) => locationKey(c) === currentKey);

  const toggleSaveCurrent = useCallback(() => {
    if (!state.location) return;
    setSavedCities((prev) => {
      const key = locationKey(state.location);
      const already = prev.find((c) => locationKey(c) === key);
      let next;
      if (already) {
        next = prev.filter((c) => locationKey(c) !== key);
      } else {
        next = [
          {
            city: state.location.city,
            admin1: state.location.admin1,
            country: state.location.country,
            country_name: state.location.country_name,
            lat: state.location.lat,
            lon: state.location.lon,
            savedAt: Date.now(),
          },
          ...prev,
        ].slice(0, SAVED_CITIES_MAX);
      }
      writeSavedCities(next);
      return next;
    });
  }, [state.location]);

  const removeSavedCity = useCallback((key) => {
    setSavedCities((prev) => {
      const next = prev.filter((c) => locationKey(c) !== key);
      writeSavedCities(next);
      return next;
    });
    // If the removed city was home, clear home too.
    setHomeKey((prev) => {
      if (prev === key) { writeHomeCityKey(null); return null; }
      return prev;
    });
  }, []);

  const toggleHomeForCurrent = useCallback(() => {
    if (!state.location) return;
    const key = locationKey(state.location);
    // Ensure the current city is in saved list too (setting as home
    // automatically pins it — otherwise "home" would evaporate on refresh).
    setSavedCities((prev) => {
      if (prev.some((c) => locationKey(c) === key)) return prev;
      const next = [
        {
          city: state.location.city, admin1: state.location.admin1,
          country: state.location.country, country_name: state.location.country_name,
          lat: state.location.lat, lon: state.location.lon,
          savedAt: Date.now(),
        },
        ...prev,
      ].slice(0, SAVED_CITIES_MAX);
      writeSavedCities(next);
      return next;
    });
    setHomeKey((prev) => {
      const next = prev === key ? null : key;
      writeHomeCityKey(next);
      return next;
    });
  }, [state.location]);

  const applyResult = useCallback((data, mode) => {
    const next = {
      phase: "ready",
      location: data.location,
      tier: data.tier,
      events: data.events || [],
      weather: data.weather || null,
      mode,
      errorMsg: null,
      fromCache: false,
    };
    setState(next);
    writeCache({
      location: next.location, tier: next.tier, events: next.events,
      weather: next.weather, mode: next.mode,
    });
  }, []);

  const runFallback = useCallback(async () => {
    // Anonymous fallback — pull the global "local" topic so the page
    // is never empty, even without GPS consent.
    try {
      const events = await npApi.byCategory("local", { limit: 20 });
      setState((s) => ({ ...s, phase: "fallback", events: events || [] }));
    } catch (e) {
      setState((s) => ({ ...s, phase: "error", errorMsg: e.message || "Could not load local news" }));
    }
  }, []);

  const requestGps = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setState((s) => ({ ...s, phase: "denied", errorMsg: "Your browser doesn't support location." }));
      runFallback();
      return;
    }
    // Preserve the already-cached data as background context while we
    // refresh — avoids a blank flash on repeat visits.
    setState((s) => ({ ...s, phase: "requesting" }));
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setState((s) => ({ ...s, phase: "resolving" }));
        try {
          const { latitude, longitude } = pos.coords;
          const data = await npApi.localHere(latitude, longitude, 25);
          applyResult(data, "gps");
        } catch (e) {
          setState((s) => ({
            ...s,
            phase: "error",
            errorMsg: e.message || "Could not load news near you.",
          }));
          runFallback();
        }
      },
      (err) => {
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        const denied = err && err.code === 1;
        setState((s) => ({
          ...s,
          phase: denied ? "denied" : "error",
          errorMsg: denied
            ? "You blocked location. Showing general local news instead."
            : "We couldn't read your location. Showing general local news instead.",
        }));
        runFallback();
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 5 * 60_000 },
    );
  }, [runFallback, applyResult]);

  const chooseCity = useCallback(async (place) => {
    setPickerOpen(false);
    setState((s) => ({ ...s, phase: "resolving" }));
    try {
      const data = await npApi.localHere(place.lat, place.lon, 25);
      applyResult(data, "manual");
    } catch (e) {
      setState((s) => ({
        ...s, phase: "error",
        errorMsg: e.message || "Could not load news for that city.",
      }));
    }
  }, [applyResult]);

  const resetToGps = useCallback(() => {
    clearCache();
    requestGps();
  }, [requestGps]);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    // If we already have a fresh cached result, keep it and DO NOT
    // trigger the geolocation prompt — that was the whole point of
    // the cache. First-time visitors (no cache) go through GPS as
    // before.
    if (state.fromCache && state.location) return;
    // Home city was seeded in the initial state — resolve it now so
    // /local opens straight into the reader's saved home without any
    // GPS prompt.
    if (state.mode === "home" && state.location) {
      chooseCity({ lat: state.location.lat, lon: state.location.lon });
      return;
    }
    requestGps();
  }, [requestGps, chooseCity, state.fromCache, state.location, state.mode]);

  const busy = state.phase === "requesting" || state.phase === "resolving";
  const locSummary = summariseLocation(state.location);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8" data-testid="np-local-page">
      <header className="mb-6 flex items-start gap-3">
        <Compass className="w-7 h-7 text-blue-600 shrink-0 mt-1" />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
            Topic
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Local
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            News near you — auto-detected from your location, cascading through
            city → state → country until there's something to read.
          </p>
        </div>
      </header>

      {/* Location banner */}
      <section
        className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5 mb-6 flex flex-wrap items-center gap-3"
        data-testid="np-local-banner"
      >
        {busy ? (
          <>
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-sm text-slate-600" data-testid="np-local-status-busy">
              {state.phase === "requesting"
                ? "Asking your browser for your location…"
                : "Finding stories near you…"}
            </span>
          </>
        ) : state.phase === "ready" && state.location ? (
          <>
            <MapPin className="w-5 h-5 text-emerald-600" />
            <div className="flex-1 min-w-0">
              <div
                className="text-sm sm:text-base font-semibold text-slate-900 truncate"
                data-testid="np-local-detected-location"
              >
                {locSummary || "Your area"}
                {state.mode === "manual" && (
                  <span
                    className="ml-2 inline-block align-middle text-[10px] uppercase tracking-widest text-slate-500 font-semibold"
                    data-testid="np-local-mode-manual"
                  >
                    · Manual pick
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {state.mode === "manual"
                  ? "You chose this city. Refresh to go back to your device location."
                  : "Detected from your device. Nothing is stored on the server."}
              </div>
            </div>
            <WeatherPill weather={state.weather} />
            <span
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border ${TIER_BADGE_STYLE[state.tier] || TIER_BADGE_STYLE.none}`}
              data-testid={`np-local-tier-${state.tier}`}
              title={`Showing news at the ${state.tier} tier`}
            >
              <Navigation className="w-3 h-3" /> {TIER_LABEL[state.tier] || state.tier}
            </span>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1 rounded-full bg-white text-blue-700 border border-blue-200 hover:border-blue-400 text-xs font-semibold px-3 py-1.5 transition-colors"
              data-testid="np-local-change-city"
            >
              <Search className="w-3 h-3" /> Change city
            </button>
            <button
              type="button"
              onClick={toggleSaveCurrent}
              className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-3 py-1.5 border transition-colors ${
                isCurrentSaved
                  ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                  : "bg-white text-amber-700 border-amber-200 hover:border-amber-500"
              }`}
              data-testid="np-local-save-city"
              aria-pressed={isCurrentSaved}
              title={isCurrentSaved ? "Remove from saved cities" : "Save this city for one-tap switching"}
            >
              {isCurrentSaved ? <Star className="w-3 h-3 fill-current" /> : <StarOff className="w-3 h-3" />}
              {isCurrentSaved ? "Saved" : "Save"}
            </button>
            <button
              type="button"
              onClick={toggleHomeForCurrent}
              className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-3 py-1.5 border transition-colors ${
                homeKey === currentKey
                  ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-500"
              }`}
              data-testid="np-local-set-home"
              aria-pressed={homeKey === currentKey}
              title={homeKey === currentKey
                ? "This is your home city — /local opens here every time"
                : "Set this city as home so /local opens here every visit"}
            >
              <Home className="w-3 h-3" />
              {homeKey === currentKey ? "Home" : "Set as home"}
            </button>
            <button
              type="button"
              onClick={resetToGps}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
              data-testid="np-local-refresh"
            >
              {state.mode === "manual" ? "Use my location" : "Refresh"}
            </button>
          </>
        ) : state.phase === "denied" ? (
          <>
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                Location access blocked
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                {state.errorMsg} You can re-enable location in your browser
                settings, or pick a city manually.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1 rounded-full bg-white text-blue-700 border border-blue-200 hover:border-blue-400 text-xs font-semibold px-3 py-1.5"
              data-testid="np-local-change-city"
            >
              <Search className="w-3 h-3" /> Choose city
            </button>
            <button
              type="button"
              onClick={requestGps}
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-1.5"
              data-testid="np-local-retry"
            >
              Try again
            </button>
          </>
        ) : (
          <>
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div className="flex-1 min-w-0 text-sm text-slate-700">
              {state.errorMsg || "Could not detect your location."}
            </div>
            <button
              type="button"
              onClick={requestGps}
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-1.5"
              data-testid="np-local-retry"
            >
              Retry
            </button>
          </>
        )}
      </section>

      {/* Saved cities chip strip — quick swipe between pinned cities. */}
      {savedCities.length > 0 && (
        <div
          className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1"
          data-testid="np-local-saved-cities"
          role="list"
          aria-label="Saved cities"
        >
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold shrink-0">
            Pinned
          </span>
          {savedCities.map((c) => {
            const key = locationKey(c);
            const active = key === currentKey;
            const isHome = key === homeKey;
            const label = (c.city || c.admin1 || c.country_name || c.country || "").trim();
            return (
              <div
                key={key}
                className={`inline-flex items-center rounded-full border text-xs font-semibold shrink-0 pl-3 pr-1 py-1 transition-colors ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}
                role="listitem"
                data-testid={`np-local-saved-city-${key.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              >
                <button
                  type="button"
                  onClick={() => chooseCity({ lat: c.lat, lon: c.lon, name: label })}
                  className="mr-1 inline-flex items-center"
                  data-testid="np-local-saved-city-switch"
                  title={`Switch to ${label}`}
                >
                  {isHome ? (
                    <Home className={`w-3 h-3 inline mr-1 ${active ? "text-amber-300" : "text-slate-500"}`} />
                  ) : (
                    <MapPin className="w-3 h-3 inline mr-1" />
                  )}
                  {label}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeSavedCity(key); }}
                  className={`w-5 h-5 inline-flex items-center justify-center rounded-full ${
                    active ? "hover:bg-slate-700" : "hover:bg-slate-100 text-slate-400"
                  }`}
                  aria-label={`Unpin ${label}`}
                  data-testid="np-local-saved-city-remove"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* "My City" strip — mandi prices + agri/climate alerts.
          Component is silent when the region has neither. */}
      {state.phase === "ready" && state.location && (
        <MyCityStrip location={state.location} />
      )}

      {/* Emergency Boards — flood / cyclone / storm alerts anchored
          to this place. Renders nothing when no active alerts. */}
      {state.phase === "ready" && state.location?.id && (
        <EmergencyBoardBanner placeId={state.location.id} className="mb-6" />
      )}

      {/* Feed */}
      {busy && state.events.length === 0 ? (
        <div className="text-sm text-slate-500 py-16 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading local news…
        </div>
      ) : state.events.length === 0 ? (
        <div
          className="text-sm text-slate-500 italic py-10 text-center"
          data-testid="np-local-empty"
        >
          No local stories found yet — check back shortly.{" "}
          <Link to="/" className="text-blue-700 underline">Back to the homepage</Link>.
        </div>
      ) : (
        <div data-testid="np-local-feed">
          {state.events.map((ev) => (
            <EventCard key={ev.event_id} event={ev} />
          ))}
        </div>
      )}

      {/* SocialPulse Publisher widget — canonical articles distributed
          to the current location. Silent when there's nothing to show. */}
      {state.phase === "ready" && state.location && (
        <PublisherWidget
          siteId="newspulse"
          locationId={publisherLocationId(state.location)}
          title="From SocialPulse Publisher — near you"
          limit={6}
          testIdPrefix="np-local-publisher"
        />
      )}

      {pickerOpen && (
        <CityPicker
          onClose={() => setPickerOpen(false)}
          onPick={chooseCity}
        />
      )}
    </div>
  );
}


/**
 * Modal city picker — debounced search against /local/geocode.
 * Keeps a small, focused UI: input + results list + cancel.
 */
function CityPicker({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const t = window.setTimeout(async () => {
      try {
        const data = await npApi.localGeocode(q.trim(), 8);
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setResults([]);
          setError(e.message || "Search failed. Try again in a moment.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [q]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Choose a city"
      onClick={onClose}
      data-testid="np-city-picker"
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a city — e.g. Kochi, Berlin, São Paulo"
            className="flex-1 outline-none border-0 text-sm placeholder:text-slate-400"
            data-testid="np-city-picker-input"
            maxLength={60}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center"
            data-testid="np-city-picker-close"
          >
            <X className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}
          {!loading && error && (
            <div className="px-4 py-6 text-sm text-red-700">{error}</div>
          )}
          {!loading && !error && q.trim().length >= 2 && results.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500 italic">
              No matching cities.
            </div>
          )}
          {!loading && q.trim().length < 2 && (
            <div className="px-4 py-6 text-xs text-slate-500">
              Start typing to search — at least two letters.
            </div>
          )}
          {results.map((r) => (
            <button
              key={`${r.lat},${r.lon}`}
              type="button"
              onClick={() => onPick(r)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 border-t border-slate-100 flex items-center gap-3"
              data-testid={`np-city-picker-result-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {r.name}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {[r.admin1, r.country_name || r.country].filter(Boolean).join(", ")}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
